import type { VulnerabilitiesState } from '../vulnerability-detection/useVulnerabilitiesData';
import type {
  WazuhVulnerabilitiesAgentSummaryData,
  WazuhVulnerabilityItem,
} from 'src/types/mindtime/wazuhVulnerabilities';
import { previewPaged } from './previewUtils';

export const PREVIEW_VULN_IDS = ['preview-vuln-1', 'preview-vuln-2'] as const;

const PREVIEW_VULNS: WazuhVulnerabilityItem[] = [
  {
    id: 'preview-vuln-1',
    cveId: 'CVE-2024-1234',
    title: 'OpenSSL buffer overflow',
    severity: 'critical',
    status: 'open',
    cvssScore: 9.8,
    packageName: 'openssl',
    recommendedFix: 'Upgrade openssl to 3.0.14 or later',
    endpoint: 'APP-SERVER-01',
    organization: 'Nexus Dynamics',
    systemWazuhAgentId: 'preview-endpoint-3',
    detectedAt: '2026-08-25T14:00:00.000Z',
    hostName: 'app-server-01.nexus.local',
  },
  {
    id: 'preview-vuln-2',
    cveId: 'CVE-2024-5678',
    title: 'Windows privilege escalation',
    severity: 'high',
    status: 'open',
    cvssScore: 8.1,
    packageName: 'windows-kernel',
    recommendedFix: 'Apply latest Windows security update',
    endpoint: 'HR-PC-17',
    organization: 'Vertex Solutions',
    systemWazuhAgentId: 'preview-endpoint-2',
    detectedAt: '2026-08-24T09:30:00.000Z',
    hostName: 'hr-pc-17.vertex.local',
  },
  {
    id: 'preview-vuln-3',
    cveId: 'CVE-2023-9999',
    title: 'Apache HTTP request smuggling',
    severity: 'medium',
    status: 'resolved',
    cvssScore: 6.5,
    packageName: 'apache2',
    recommendedFix: 'Upgrade apache2 to 2.4.58',
    endpoint: 'WEB-APP-07',
    organization: 'Acme Financial',
    systemWazuhAgentId: 'preview-endpoint-5',
    detectedAt: '2026-08-20T11:15:00.000Z',
    hostName: 'web-app-07.acme.local',
  },
];

export const getVulnerabilitiesPreviewState = (): VulnerabilitiesState => ({
  loading: false,
  summary: {
    totalVulnerabilities: 142,
    activeVulnerabilities: 89,
    resolvedVulnerabilities: 53,
    criticalVulnerabilities: 12,
    activeCriticalVulnerabilities: 8,
    resolvedCriticalVulnerabilities: 4,
    highRiskEndpoints: 5,
    remediationPercent: 37,
  },
  items: previewPaged(PREVIEW_VULNS, 0, 15),
  endpoints: previewPaged(
    [
      {
        systemWazuhAgentId: 'preview-endpoint-3',
        endpoint: 'APP-SERVER-01',
        hostName: 'app-server-01.nexus.local',
        organization: 'Nexus Dynamics',
        totalVulnerabilities: 14,
        open: 11,
        severity: 'critical',
        topCvss: 9.8,
      },
      {
        systemWazuhAgentId: 'preview-endpoint-2',
        endpoint: 'HR-PC-17',
        hostName: 'hr-pc-17.vertex.local',
        organization: 'Vertex Solutions',
        totalVulnerabilities: 8,
        open: 6,
        severity: 'high',
        topCvss: 8.1,
      },
    ],
    0,
    10,
  ),
  facets: {
    operatingSystems: ['Windows', 'Linux'],
    packages: ['openssl', 'apache2', 'windows-kernel'],
  },
});

export const getVulnerabilityDetailPreview = (id: string): WazuhVulnerabilityItem | null =>
  PREVIEW_VULNS.find((row) => row.id === id) ?? PREVIEW_VULNS[0] ?? null;

export const getAgentVulnerabilitiesPreview = (
  systemWazuhAgentId: string,
): { summary: WazuhVulnerabilitiesAgentSummaryData; items: WazuhVulnerabilityItem[] } => {
  const agentId = systemWazuhAgentId.trim();
  const items = PREVIEW_VULNS.filter((row) => row.systemWazuhAgentId === agentId);
  const list = items.length ? items : PREVIEW_VULNS.slice(0, 2);
  const open = list.filter((row) => row.status === 'open').length;

  return {
    summary: {
      systemWazuhAgentId: agentId,
      total: list.length,
      open,
      closed: list.length - open,
      critical: { open: 1, closed: 0, total: 1 },
      high: { open: 1, closed: 0, total: 1 },
      medium: { open: 0, closed: 1, total: 1 },
      low: { open: 0, closed: 0, total: 0 },
      remediationPercent: 33,
      addressed: list.length - open,
      meta: { lastSyncedAt: '2026-08-28T10:00:00.000Z' },
    },
    items: list,
  };
};
